import { useState, useEffect } from 'react';
import { fetchCMSGalleryBySlug } from '../lib/cms';
import { fallbackGallery } from '../data';

export default function GallerySection() {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    async function loadGallery() {
      try {
        const data = await fetchCMSGalleryBySlug('klinik-og-behandling');
        if (data && data.images && data.images.length > 0) {
          setGallery(data);
        } else {
          setGallery(fallbackGallery);
        }
      } catch (error) {
        console.error('Failed to load gallery from CMS, using fallback:', error);
        setGallery(fallbackGallery);
      } finally {
        setLoading(false);
      }
    }
    loadGallery();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-sage border-t-transparent" />
      </div>
    );
  }

  if (!gallery || !gallery.images || gallery.images.length === 0) {
    return null;
  }

  // Helper to get optimized Cloudflare image URL
  const getOptimizedUrl = (url, width = 600) => {
    if (url.startsWith('https://assets.auxodev.dk/')) {
      return url.replace(
        'https://assets.auxodev.dk/assets/',
        `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=80,format=auto/assets/`
      );
    }
    return url;
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev === 0 ? gallery.images.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev === gallery.images.length - 1 ? 0 : prev + 1));
  };

  return (
    <section className="py-12 md:py-16 bg-brand-sage-light/30 rounded-3xl px-6 md:px-10">
      <div className="mx-auto max-w-5xl text-center">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-sage-dark">
          Stemning & Rammer
        </span>
        <h2 className="mt-2 font-serif text-3xl font-semibold text-brand-neutral-dark md:text-4xl">
          {gallery.title?.da || gallery.title || 'Klinik & Behandling'}
        </h2>
        {gallery.description?.da || gallery.description ? (
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-brand-neutral-dark/80">
            {gallery.description?.da || gallery.description}
          </p>
        ) : null}
      </div>

      {/* Image Grid */}
      <div className="mx-auto mt-10 grid max-w-5xl grid-cols-2 gap-4 sm:grid-cols-3 md:gap-6">
        {gallery.images.map((imgUrl, index) => (
          <button
            key={index}
            onClick={() => setActiveIndex(index)}
            className="group relative aspect-square overflow-hidden rounded-2xl bg-brand-cream/50 focus:outline-none focus:ring-2 focus:ring-brand-sage focus:ring-offset-2"
            aria-label={`Vis billede ${index + 1} i fuld størrelse`}
          >
            <img
              src={getOptimizedUrl(imgUrl, 500)}
              alt={`Klinik stemningsbillede ${index + 1}`}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-brand-neutral-dark/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-brand-sage shadow-md">
                <i className="fa-regular fa-magnifying-glass-plus" />
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Lightbox Modal */}
      {activeIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-brand-neutral-dark/90 p-4 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setActiveIndex(null)}
        >
          {/* Close Button */}
          <button
            onClick={() => setActiveIndex(null)}
            className="absolute top-6 right-6 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors duration-300 focus:outline-none"
            aria-label="Luk visning"
          >
            <i className="fa-light fa-xmark text-xl" />
          </button>

          {/* Prev Button */}
          <button
            onClick={handlePrev}
            className="absolute left-4 md:left-8 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors duration-300 focus:outline-none"
            aria-label="Forrige billede"
          >
            <i className="fa-light fa-chevron-left text-lg" />
          </button>

          {/* Main Image */}
          <div className="relative max-h-[80vh] max-w-[90vw] overflow-hidden rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <img
              src={getOptimizedUrl(gallery.images[activeIndex], 1200)}
              alt={`Fuld størrelse stemningsbillede ${activeIndex + 1}`}
              className="max-h-[80vh] max-w-[90vw] object-contain"
            />
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-brand-neutral-dark/60 px-4 py-1.5 text-xs text-white">
              {activeIndex + 1} / {gallery.images.length}
            </div>
          </div>

          {/* Next Button */}
          <button
            onClick={handleNext}
            className="absolute right-4 md:right-8 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors duration-300 focus:outline-none"
            aria-label="Næste billede"
          >
            <i className="fa-light fa-chevron-right text-lg" />
          </button>
        </div>
      )}
    </section>
  );
}
